/**
 * LcdSurveyModal
 * Mandatory 3-field survey shown after a BM picks an adopted LCD and clicks
 * Check In. Cannot be dismissed via backdrop/Escape — submitting the survey is
 * what triggers the actual Salesforce check-in (scope §5.1).
 *
 * Styling mirrors the PWA's existing MUI dialog pattern (gradient brand header,
 * @shared/CustomButton for actions, MUI TextField for inputs).
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CustomButton } from "@shared/components";

const FIELDS = [
  { key: "reasonForCompetitionPresence", label: "Reason for Competition Presence" },
  { key: "actionPlanForShareGain",       label: "Action Plan for Share Gain" },
  { key: "updateAgainstPlannedActions",  label: "Update against Planned Actions" },
];

const MIN_LEN = 50;
const MAX_LEN = 255;

const emptyAnswers = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const LcdSurveyModal = ({ isOpen, submitting, context, onSubmit }) => {
  const [answers, setAnswers] = useState(emptyAnswers);
  const [touched, setTouched] = useState({});

  // Pre-fill from the most recent prior submission whenever the modal opens
  // for a new dealer. Reset to blank when there's no prior survey on file.
  useEffect(() => {
    if (!isOpen) return;
    const prev = context?.previousAnswers;
    if (prev) {
      setAnswers({
        reasonForCompetitionPresence: prev.reasonForCompetitionPresence || "",
        actionPlanForShareGain: prev.actionPlanForShareGain || "",
        updateAgainstPlannedActions: prev.updateAgainstPlannedActions || "",
      });
    } else {
      setAnswers(emptyAnswers);
    }
    setTouched({});
  }, [isOpen, context?.dealerCode, context?.previousAnswers]);

  const errors = useMemo(() => {
    const errs = {};
    for (const f of FIELDS) {
      const v = (answers[f.key] || "").trim();
      if (v.length < MIN_LEN) errs[f.key] = `Minimum ${MIN_LEN} characters required`;
      else if (v.length > MAX_LEN) errs[f.key] = `Maximum ${MAX_LEN} characters allowed`;
    }
    return errs;
  }, [answers]);

  const isFormValid = Object.keys(errors).length === 0;

  const handleChange = useCallback((key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleBlur = useCallback((key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setTouched(FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: true }), {}));
    if (!isFormValid) return;
    const result = await onSubmit(answers);
    if (result?.success) {
      setAnswers(emptyAnswers);
      setTouched({});
    }
  }, [answers, isFormValid, onSubmit]);

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        // Mandatory: block backdrop click + Escape.
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          mx: { xs: 1, sm: "auto" },
          my: { xs: 2, sm: "auto" },
        },
      }}
    >
      {/* ── Header (brand gradient, matches OAAP/PWA pattern) ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #3901ff 0%, #6a3de8 100%)",
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              backgroundColor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StoreOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                color: "#fff",
                lineHeight: 1.25,
              }}
            >
              LCD Survey
            </Typography>
            {context?.dealerName ? (
              <Typography
                noWrap
                sx={{
                  fontSize: { xs: "0.72rem", sm: "0.78rem" },
                  color: "rgba(255,255,255,0.78)",
                  mt: 0.25,
                }}
              >
                {context.dealerName}
                {context?.dealerCode ? ` · ${context.dealerCode}` : ""}
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Chip
          icon={<LockOutlinedIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
          label="Mandatory"
          size="small"
          sx={{
            backgroundColor: "rgba(255,255,255,0.18)",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.65rem",
            height: 22,
            border: "1px solid rgba(255,255,255,0.25)",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Typography
          sx={{
            fontSize: "0.82rem",
            color: "#5a5a5a",
            mb: 2,
            lineHeight: 1.5,
          }}
        >
          {context?.previousAnswers
            ? "Your previous response has been loaded — update what's changed and submit. "
            : "Please share inputs for this LCD before the check-in is completed. "}
          All fields are required ({MIN_LEN}–{MAX_LEN} characters).
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {FIELDS.map((f, idx) => {
            const value = answers[f.key] || "";
            const showError = touched[f.key] && !!errors[f.key];
            return (
              <Box key={f.key}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: "#2b2b2b",
                    }}
                  >
                    {idx + 1}. {f.label}
                    <Box component="span" sx={{ color: "#e60000", ml: 0.25 }}>
                      *
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: value.length > MAX_LEN ? "#e60000" : "#9a9a9a",
                      fontWeight: 500,
                    }}
                  >
                    {value.length}/{MAX_LEN}
                  </Typography>
                </Box>
                <TextField
                  value={value}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  onBlur={() => handleBlur(f.key)}
                  multiline
                  minRows={2}
                  maxRows={5}
                  fullWidth
                  size="small"
                  placeholder="Type your response here..."
                  inputProps={{ maxLength: MAX_LEN }}
                  error={showError}
                  helperText={showError ? errors[f.key] : " "}
                  disabled={submitting}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1.5,
                      backgroundColor: "#fafafa",
                      fontSize: "0.85rem",
                      "& fieldset": { borderColor: "#e0e0e0" },
                      "&:hover fieldset": { borderColor: "#bdbdbd" },
                      "&.Mui-focused fieldset": { borderColor: "#3901ff", borderWidth: 1.5 },
                    },
                    "& .MuiFormHelperText-root": {
                      mx: 0.25,
                      mt: 0.5,
                      fontSize: "0.68rem",
                      minHeight: "0.9rem",
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: "#eee" }} />

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <CustomButton
          variant="primary"
          onClick={handleSubmit}
          disabled={submitting || !isFormValid}
          loading={submitting}
          fullWidth
          size="md"
        >
          {submitting ? "Submitting…" : "Submit & Check In"}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default LcdSurveyModal;
